import { useEffect, useRef } from 'react';
import useCustomHook from '@/hooks/useCustomHook';
import { getRandomInteger } from '@/modules/gameUtils/random';

const failureSoundsUrls = [
  process.env.NEXT_PUBLIC_CLOUDFRONT_STATIC_HOST +
    '/commonGameAssets/classic/sounds/oo.mp3',
  process.env.NEXT_PUBLIC_CLOUDFRONT_STATIC_HOST +
    '/commonGameAssets/classic/sounds/oooo.mp3',
];

export default function useQuestionInteraction({
  interaction,
  playerState,
  playerActions,
  detectedCues,
  pages,
}) {
  const failureSoundsRef = useRef([]);
  const { conf } = interaction;
  const { boxIds } = conf;
  const lastBox = boxIds[boxIds.length - 1];
  const firstBox = boxIds[0];

  const { state, actions } = useCustomHook({
    defaultState: {
      interactionActive: false,
      failures: [],
    },
    actions: {
      onOptionClick: (boxId) => {
        if (boxId == conf.correctBoxId) {
          actions.goToPageCue(conf.correctRedirect);
        } else {
          // Two options usually are 'Yes' and 'No', so we don't want to play the error sound
          // It should be configured in the interaction configuration
          if (boxIds?.length > 2) {
            actions.playErrorSound();
          }

          const failures = [...state.failures];
          if (!state.failures.includes(boxId)) {
            failures.push(boxId);
          }

          if (failures.length == boxIds.length - 1) {
            actions.goToPageCue(conf.failureRedirect);
          } else {
            actions.setState({
              failures,
            });
          }
        }
      },
      goToPageCue: (cue) => {
        const nextPage = pages[cue.pageId];
        playerActions.setState({
          currentPage: nextPage,
          currentBox: null,
          currentBubble: null,
          playing: !playerState.readMode,
          playingBubble: false,
          stopAt: null,
          seekTo: 0,
          playerReady: false,
          pageImageLoaded: false,
          playModeReady: false,
          endOfPageReached: false,
          comingFromPageId: playerState.currentPage.id,
        });
      },
      playErrorSound: () => {
        try {
          const failureSound =
            failureSoundsRef.current[
              getRandomInteger(0, failureSoundsRef.current.length - 1)
            ];
          failureSound.play();
        } catch (e) {
          console.error('Error playing sound', e);
        }
      },
    },
  });

  useEffect(() => {
    failureSoundsRef.current = failureSoundsUrls.map((src) => {
      const audio = new Audio(src);
      audio.load();
      return audio;
    });
  }, []);

  useEffect(() => {
    if (playerState.readMode) {
      actions.setState({
        interactionActive: true,
      });
    } else {
      actions.setState({
        interactionActive: false,
      });
    }
  }, [playerState.readMode]);

  useEffect(() => {
    for (const cue of detectedCues) {
      if (cue.status == 'STARTING' && cue.type == 'page') {
        actions.setState({
          interactionActive: false,
        });
      }
      if (cue.status == 'STARTING' && cue.boxId == firstBox) {
        actions.setState({
          interactionActive: true,
        });
      }
    }
  }, [detectedCues]);

  useEffect(() => {
    if (state.interactionActive) {
      actions.setState({
        failures: [],
      });
    }
  }, [state.interactionActive]);

  return { state, actions };
}
